import React, { useEffect, useState } from 'react'
import zyeatlogo from '../../assets/zyeat-removebg.png'
import { Link } from 'react-router-dom'
import cartService, { CART_CHANGED_EVENT } from '../../services/cartService'
import { getSelectedGym, GYM_CHANGED_EVENT } from '../../utils/gym'

export default function Header () {
  const [cartCount, setCartCount] = useState(0)
  const [gym, setGym] = useState(getSelectedGym())

  // Keep the gym chip in sync with the client-side selected gym.
  useEffect(() => {
    const sync = () => setGym(getSelectedGym())
    window.addEventListener(GYM_CHANGED_EVENT, sync)
    window.addEventListener('focus', sync)
    return () => {
      window.removeEventListener(GYM_CHANGED_EVENT, sync)
      window.removeEventListener('focus', sync)
    }
  }, [])

  // Live cart badge. Fetch once on mount, then refresh whenever the cart changes
  // (cartService emits CART_CHANGED_EVENT after every mutation) or the window
  // regains focus. No polling, no store — reuses the existing cart source.
  useEffect(() => {
    let active = true
    const refresh = async () => {
      try {
        const res = await cartService.getCart()
        const items = res?.cart?.items || res?.data?.cart?.items || []
        const count = Array.isArray(items)
          ? items.reduce((sum, i) => sum + Number(i.quantity || 1), 0)
          : 0
        if (active) setCartCount(count)
      } catch (e) {
        // not authenticated / no cart yet — leave the count as-is
      }
    }
    refresh()
    window.addEventListener(CART_CHANGED_EVENT, refresh)
    window.addEventListener('focus', refresh)
    return () => {
      active = false
      window.removeEventListener(CART_CHANGED_EVENT, refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [])

  return (
    <div className='fixed top-0 z-50 w-full bg-white px-5 py-4 flex justify-between items-center'>
      {/* Left Side: Logo */}
      <div className='flex items-center gap-3'>
        <Link to='/'>
          <img loading='lazy' decoding='async' src={zyeatlogo} alt='logo' className='w-20 h-10' />
        </Link>
        {/* Selected pickup gym (unobtrusive) — tap to change. */}
        <Link to='/select-gym' aria-label='Change gym'
          className='flex items-center gap-1 max-w-[42vw] text-[11px] font-semibold text-[#065c2d] bg-[#EEF8F1] border border-[#cbe8d6] rounded-full px-2 py-1'>
          <span className='truncate'>🏋️ {gym ? gym.name : 'Select gym'}</span>
        </Link>
      </div>

      {/* Right Side: utility actions — Cart, then Notification */}
      <div className='flex items-center gap-4'>
        {/* Cart */}
        <Link
          to='/cart'
          aria-label={cartCount > 0 ? `Cart, ${cartCount} items` : 'Cart'}
          className='relative text-gray-800 hover:text-[#0D6E26] transition-colors'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth='1.5'
            stroke='currentColor'
            className='w-6 h-6'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M2.25 3h1.386c.51 0 .955.343 1.087.836l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z'
            />
          </svg>
          {cartCount > 0 && (
            <span className='absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF7043] text-white text-[10px] font-bold flex items-center justify-center'>
              {cartCount}
            </span>
          )}
        </Link>

        {/* Notification Bell (unchanged) */}
        <button className='text-gray-800 hover:text-[#0D6E26] transition-colors'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth='1.5'
            stroke='currentColor'
            className='w-6 h-6'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0'
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
